import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import GroupsIcon from "@mui/icons-material/Groups";
import PoolIcon from "@mui/icons-material/Pool";
import EventIcon from "@mui/icons-material/Event";
import UserListItem from "./UserListItem";

const useStyles = makeStyles((theme) => ({
  poolCard: {
    borderRadius: "16px",
    border: "2px solid #e0e0e0",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
      borderColor: "#1976d2",
    },
  },
  cardHeader: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "20px 24px",
    color: "#fff",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  poolTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  poolIdText: {
    fontWeight: 700,
    fontSize: "1.1rem",
    fontFamily: "monospace",
    wordBreak: "break-all",
  },
  userCountChip: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    fontWeight: 600,
    border: "1px solid rgba(255, 255, 255, 0.3)",
  },
  leagueTypeBadge: {
    display: "inline-block",
    padding: "4px 12px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: "12px",
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  cardContent: {
    padding: "24px !important",
    backgroundColor: "#f8f9fa",
  },
  usersList: {
    maxHeight: "600px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: theme.palette.text.secondary,
  },
  statsRow: {
    display: "flex",
    gap: "16px",
    marginTop: "12px",
    flexWrap: "wrap",
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "rgba(255, 255, 255, 0.9)",
    fontSize: "0.875rem",
  },
}));

const LEAGUE_COLORS = {
  STARTER: "#4caf50",
  BRONZE: "#8d6e63",
  BRONZE_II: "#795548",
  SILVER: "#9e9e9e",
  SILVER_II: "#607d8b",
  GOLD: "#ffc107",
  GOLD_II: "#ff9800",
  DIAMOND: "#2196f3",
  DIAMOND_II: "#3f51b5",
  PLATINUM: "#9c27b0",
};

const PoolCard = ({ pool, highlightedUserId, userRef }: any) => {
  const styles = useStyles();

  // Use users as returned by API without sorting
  const users = pool.users || [];

  const botCount = users.filter((u) => u.isBot).length;
  const humanCount = users.filter((u) => !u.isBot).length;

  // Format dates
  const formatDate = (dateString: any) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card className={styles.poolCard}>
      <Box className={styles.cardHeader}>
        <Box className={styles.headerContent}>
          <Box className={styles.poolTitle}>
            <PoolIcon fontSize="large" />
            <Box flex={1}>
              <Typography className={styles.poolIdText}>
                Pool: {pool.poolId}
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={<GroupsIcon />}
            label={`${pool.userCount} Users`}
            className={styles.userCountChip}
          />
        </Box>

        <Box className={styles.statsRow}>
          <Box className={styles.statItem}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: LEAGUE_COLORS[pool.leagueType] || "#666",
              }}
            />
            <span className={styles.leagueTypeBadge}>
              {pool.leagueType.replace(/_/g, " ")}
            </span>
          </Box>
          <Box className={styles.statItem}>
            <span>🤖 Bots: {botCount}</span>
          </Box>
          <Box className={styles.statItem}>
            <span>👤 Humans: {humanCount}</span>
          </Box>
        </Box>

        {/* Pool Date Range */}
        {(pool.startDate || pool.endDate) && (
          <Box className={styles.statsRow} sx={{ marginTop: "8px" }}>
            <Box className={styles.statItem}>
              <EventIcon fontSize="small" />
              <span>
                {formatDate(pool.startDate)} - {formatDate(pool.endDate)}
              </span>
            </Box>
          </Box>
        )}

        {/* Season and Cycle */}
        {(pool.season || pool.cycleNumber) && (
          <Box className={styles.statsRow} sx={{ marginTop: "8px" }}>
            {pool.season && (
              <Box className={styles.statItem}>
                <span>🏆 Season: {pool.season}</span>
              </Box>
            )}
            {pool.cycleNumber && (
              <Box className={styles.statItem}>
                <span>🔄 Cycle: {pool.cycleNumber}</span>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <CardContent className={styles.cardContent}>
        {users.length > 0 ? (
          <Box className={styles.usersList}>
            {users.map((user) => (
              <UserListItem
                key={user.userId}
                user={user}
                isHighlighted={highlightedUserId === user.userId}
                ref={highlightedUserId === user.userId ? userRef : null}
              />
            ))}
          </Box>
        ) : (
          <Box className={styles.emptyState}>
            <GroupsIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <Typography variant="body1">No users in this pool</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PoolCard;
